import { useCallback } from 'react';
import {
  useRouter,
  type AnyRouter,
  type NavigateOptions,
} from '@tanstack/react-router';

/**
 * LEAVING A SCREEN NEVER PUSHES (docs/5 § D260809-0359).
 *
 * The bug this exists for: you tap a letter, tap *Comment*, send it, and the back
 * button puts you back in the composer you just sent from. The history was
 * `feed → letter → compose → letter`, four entries for two places, because the
 * composer's send did a plain `navigate` and a plain `navigate` pushes.
 *
 * A composer is a DETOUR rather than a destination. It is entered from somewhere,
 * it is left, and the entry it occupied should not survive the leaving — so the
 * back button means the place you were reading, at the scroll you left it at, and
 * never the words you already sent.
 *
 * TWO WAYS TO LEAVE, AND THE RULE PICKS BETWEEN THEM:
 *
 *   - **Unwind** (`history.back()`) when the destination is the entry the detour
 *     was pushed on top of. This is the common case — you comment on a letter from
 *     that letter's own screen — and it is the only one of the two that brings the
 *     surface back rather than building a new one: its scroll position, and every
 *     entry below it, are exactly as they were.
 *   - **Replace** otherwise. Sending a direct letter lands you in the
 *     correspondence, and commenting from a room lands you in the conversation,
 *     neither of which is where you started. Replacing puts the destination in the
 *     composer's own slot, so one back press still reaches the room, scrolled.
 *
 * NEITHER OF THEM IS A PUSH, which is the whole of the fix. `replace: true` alone
 * would have been most of it and left the common case broken: replacing the
 * composer with the letter it answers makes two adjacent entries for one letter,
 * and back presses the first one for nothing.
 *
 * WHY A TRAIL RATHER THAN A ROUTER READ. Nothing on `router.history` says what is
 * underneath the current entry — `canGoBack` says only that something is, and
 * `fromLocation` on a router event is the entry you LEFT, which after a back
 * navigation is the one above. The browser will not be asked, by design. So the
 * app keeps its own note: `__TSR_index` is the depth TanStack already stamps into
 * every history entry, and this records which address was at each depth.
 *
 * `sessionStorage` FOR THE SAME REASON THE SCROLL CACHE USES IT: a reload keeps
 * the history stack, so a trail held in a module variable would come back empty
 * against a stack that did not, and every leave would degrade to a replace. It is
 * per-tab and it is thrown away with the tab, which is exactly the lifetime of the
 * stack it describes.
 */
const TRAIL_KEY = 'dove-history-trail-v1';

/** Address by history depth. Sparse and small — one short string per entry. */
type Trail = Record<string, string>;

function readTrail(): Trail {
  try {
    return JSON.parse(sessionStorage.getItem(TRAIL_KEY) ?? '{}') as Trail;
  } catch {
    /* Private mode, a storage quota, or a half-written value. A trail is an
       optimisation over `replace`, so losing it costs a back press and never a
       screen. */
    return {};
  }
}

function writeTrail(trail: Trail): void {
  try {
    sessionStorage.setItem(TRAIL_KEY, JSON.stringify(trail));
  } catch {
    /* See above. */
  }
}

/** How deep the current entry is. TanStack stamps this on every entry it makes. */
function depth(router: AnyRouter): number {
  const state = router.history.location.state as { __TSR_index?: number };
  return state.__TSR_index ?? 0;
}

/**
 * Start recording the trail. Called once, on the router, in `router.tsx`.
 *
 * NOTHING IS EVER DELETED. A push overwrites the depth it lands on, which is the
 * only staleness that matters: the entries a push discarded sit above it and are
 * unreachable until something navigates there again, and that navigation records
 * itself. Only `depth - 1` is ever read.
 */
export function trackHistoryTrail(router: AnyRouter): void {
  const record = () => {
    const trail = readTrail();
    trail[String(depth(router))] = router.history.location.href;
    writeTrail(trail);
  };

  /* The entry the app opened on, which no event will announce. */
  record();
  router.subscribe('onResolved', record);
}

/**
 * Leave the screen you are on for `to`, without leaving it behind.
 *
 * Used by every exit from `/compose` — both sends and Cancel — and available to
 * any other screen that is a detour rather than a place.
 */
export function useLeaveFor(): (to: NavigateOptions) => void {
  const router = useRouter();

  return useCallback(
    (to: NavigateOptions) => {
      const here = depth(router);
      const beneath = readTrail()[String(here - 1)];

      /* `buildLocation` rather than a hand-built string, because the destination
         arrives as a route and its params and only the router knows how those two
         become an address — the same call `Link` makes to decide what it points at. */
      if (here > 0 && beneath && beneath === router.buildLocation(to).href) {
        router.history.back();
        return;
      }

      void router.navigate({ ...to, replace: true });
    },
    [router],
  );
}
